/* eslint-disable max-len */
/* eslint-disable @next/next/no-img-element */
import useCommon from '@utils/hooks/common';
import {Col, Form, Row, Spin, Statistic, notification, Skeleton as AntSkeleton} from 'antd';
import CustomInput from '@components/CustomInput';
import {useEffect, useState} from 'react';
import useRequest from '@utils/hooks/useRequestCustom';
import authApi from '@utils/api/auth.api';
import {CopyOutlined, LoadingOutlined} from '@ant-design/icons';
import CustomSelect from '@components/CustomSelect';
import GradientButton from '@components/GradientButton';
import {QRCode} from 'react-qrcode-logo';
import styles from './styles';
import {env} from 'next-runtime-env';
import BigNumber from 'bignumber.js';
import getTokenSymbol from '../getTokenSymbol';
import {useRouter} from 'next/router';
import {
  TonConnectButton,
  useTonAddress,
  useTonConnectModal,
  useTonConnectUI,
  useTonWallet,
} from '@tonconnect/ui-react';
import {beginCell, toNano, Address} from '@ton/ton';
import axios from 'axios';
import {isTelegramMiniApp} from '@components/layout';
import DepositMethodSwitch from '@components/telegram/DepositMethodSwitch';
import EmbossedContainer from '@components/EmbossedContainer';
import {copyTextToClipboard, trackCustomEvent} from '@utils/index';

interface Network {
  network: string;
  token: string;
  fee: string;
  decimalUnit: number;
  contractAddress: `0x${string}`;
  networkTokenId: any;
}

interface BalanceInfo {
  decimals: number;
  formatted: string;
  symbol: string;
  value: bigint;
}

interface Balances {
  [key: string]: BalanceInfo;
}

const NEXT_PUBLIC_FRONTEND_HOST_URL = env('NEXT_PUBLIC_FRONTEND_HOST_URL');

export const productionURL = 'https://win4.poker';

export const isProd = NEXT_PUBLIC_FRONTEND_HOST_URL === productionURL;

console.log(
  'TelegramContent - isProd, NEXT_PUBLIC_FRONTEND_HOST_URL:',
  isProd,
  NEXT_PUBLIC_FRONTEND_HOST_URL
);

export const DEFAULT_NETWORK_TOKEN_ID = 'USDT_ETH';

const TON_DEFAULT_TOKEN = 'USDT_TON';

const FORM_INITIAL_VALUES = {blockchain: 'usdt'};

const TelegramContent = () => {
  const {classes, t, user, system} = useCommon({
    withTranslation: ['deposit_withdraw'],
    withStyles: styles,
    observers: ['user', 'system'],
  });

  const router = useRouter();
  const isDebug = router && router.query.isDebug === 'true';

  const [form] = Form.useForm();

  const [networks, setNetworks] = useState<Network[]>([]);
  const [selectedToken, setSelectedToken] = useState<
    'USDT_ETH' | 'USDT_BSC' | 'USDT_TRX' | 'USDT_TON'
  >('USDT_ETH');
  const [regType, setRegType] = useState<'WALLET' | 'METAMASK' | 'EMAIL'>(null);
  const [clickCount, setClickCount] = useState(0);
  const [usdtTokenBalance, setUsdtTokenBalance] = useState<number | null>(null);
  const [isManualDeposit, setIsManualDeposit] = useState(false);

  const [tonConnectUI, setOptions] = useTonConnectUI();

  const userFriendlyAddress = useTonAddress();
  const rawAddress = useTonAddress(false);
  const wallet = useTonWallet();

  const decimal = 6;
  const TON_CONSOLE_API_KEY =
    'AEPYJB2LFOKPNHIAAAANDZRRFECMNG3K2H2GZGAPU7SOH4RFIEPDVVAQHZOTN764LBO77OQ';
  const TON_CENTER_BOT_API_KEY = 'c4a137cc43cf2dab7a4be2aa9b9be91258ac0bf9718e1791f5a8e5d9cd662e81';
  const TON_API_BASE_URL = isProd ? 'https://tonapi.io/v2' : 'https://testnet.tonapi.io/v2';

  const [jettonMasterAddress, setJettonMasterAddress] = useState<string | null>(null);
  const [jettonWalletAddress, setJettonWalletAddress] = useState<string | null>(null);
  const [jettonDecimals, setJettonDecimals] = useState<number | null>(null);

  // console.log('depositTab - isProd, NEXT_PUBLIC_URL, router', isProd, NEXT_PUBLIC_URL, router);

  const {
    data,
    loading: loadingDepositAddress,
    runAsync: getDepositAddress,
  } = useRequest(authApi.depositAddress, {
    onSuccess: result => {
      form.setFieldValue('depositAddress', result?.data?.walletAddress);
    },
  });

  const {data: depositAddress} = data || {};
  const {memo, walletAddress} = depositAddress || {};

  // * fetch jetton master address
  const fetchJettonMasterAddress = async () => {
    try {
      const endpoint = `${TON_API_BASE_URL}/accounts/${userFriendlyAddress}/jettons`;
      const response = await axios.get(endpoint, {
        params: {
          // TODO @@ dont hard code
          // * for staging: '0QC9Ldrj397HAWEFCOI6DK4IM9taQAtD9dQvzWLNrax37hd1'
          address: walletAddress, // Jetton wallet address
        },
      });

      const usdtBalance = response.data.balances.find(
        item => item.jetton.name === (isProd ? 'Tether USD' : 'USDT')
      );
      console.log('fetchJettonMasterAddress, usdtBalance', usdtBalance);
      setJettonMasterAddress(usdtBalance.jetton.address);
    } catch (error) {
      console.error('Error fetching Jetton balance:', error);
    }
  };

  // TODO @@ can use getTokenData from toncenter to check contract_type
  // * https://toncenter.com/api/v2/#/accounts/detect_address_detectAddress_get

  const convertJettonBalance = jettonData => {
    const rawBalance = jettonData.balance; // Raw balance as a string
    const decimals = jettonData.jetton.decimals; // Decimals specific to the jetton
    // const formattedBalance = (Number(rawBalance) / Math.pow(10, decimals)).toLocaleString(
    //   undefined,
    //   {
    //     minimumFractionDigits: 0, // Minimum decimal places
    //     maximumFractionDigits: decimals, // Maximum decimal places based on jetton's decimals
    //   }
    // );

    // return `${formattedBalance} ${jettonData.jetton.symbol}`; // Combine with the symbol for display
    const formattedBalance = new BigNumber(rawBalance).dividedBy(10 ** decimals).toNumber();
    return formattedBalance;
  };

  // * for getting usdt token in wallet
  const fetchUsdtToken = async () => {
    try {
      const response = await fetch(`${TON_API_BASE_URL}/accounts/${userFriendlyAddress}/jettons`);
      const data = await response.json();
      console.log('useEffect - fetchUsdtToken, data:', data);

      const usdtBalance = data.balances.find(
        item => item.jetton.name === (isProd ? 'Tether USD' : 'USDT')
      );
      console.log('fetchUsdtToken - usdtBalance:', usdtBalance);

      // if (data.balances[0]?.jetton.name === (isDev ? 'USDT' : 'Tether USD')) {
      if (usdtBalance) {
        const {balance} = usdtBalance;

        const converted = convertJettonBalance(usdtBalance);
        // const temp1 = Number(10000000000000) / Math.pow(10, 9);
        // console.log('formattedBalance', formattedBalance);
        setUsdtTokenBalance(converted);
        const {address} = usdtBalance.wallet_address;
        // const {address} = data.balances[0]?.wallet_address;
        setJettonWalletAddress(address);
        setJettonDecimals(usdtBalance.jetton.decimals);
        // console.log('formattedBalance', formattedBalance);
      }
    } catch (error) {
      console.error('Error fetching balance:', error);
    }
  };

  useEffect(() => {
    if (userFriendlyAddress) {
      console.log(' have userFriendlyAddress => call fetchUSDT & fetchJettonBalance ');
      fetchUsdtToken();
      fetchJettonMasterAddress();
    }
  }, [userFriendlyAddress]);

  // TODO @@ to true for dev
  const isTg = isTelegramMiniApp;
  // const isTg = true;

  const {data: depositInfo, loading: loadingDepositInfo} = useRequest(authApi.getDepositInfo, {
    onSuccess: result => {
      // console.log('@@ result?.networks', result?.networks);
      if (isDebug) {
        setNetworks(MOCK_NETWORKS);
      } else {
        // setNetworks(result?.networks?.filter(item => item.networkTokenId === 'USDT_TON'));
        setNetworks(result?.networks);
      }
      setRegType(result?.registrationType);
      if (isTg) {
        form.setFieldValue('network', TON_DEFAULT_TOKEN);
      }
    },
  });

  useEffect(() => {
    if (isTg) {
      setSelectedToken(TON_DEFAULT_TOKEN);
      getDepositAddress({networkTokenId: TON_DEFAULT_TOKEN});
      form.setFieldValue('network', TON_DEFAULT_TOKEN);
    }
  }, [isTg]);

  const toggleSwitch = () => {
    setIsManualDeposit(!isManualDeposit);
  };

  const handleCopyClick = async value => {
    if (!value) return;

    try {
      await copyTextToClipboard(value);
      notification.success({message: t('common:copy_success')});
    } catch (error) {
      console.error('Error copying text:', error);
    }
  };

  if (loadingDepositInfo || !regType) return <AntSkeleton paragraph={{rows: 5}} active />;

  return (
    <section className={classes.centeredContainer}>
      <Form
        className={classes.formContainer}
        layout="vertical"
        initialValues={FORM_INITIAL_VALUES}
        form={form}
        onValuesChange={async (changedValues, values) => {
          // console.log('values', changedValues, values);
          const {blockchain, network} = values;
          if (blockchain && network) {
            // setDepositAddress();
          } else {
            form.setFieldValue('depositAddress', '');
            // setQrValue(null);
          }
        }}
        name="deposit"
        scrollToFirstError
        requiredMark={false}
        onFinish={async values => {
          trackCustomEvent({
            eventName: 'telegram_deposit',
            params: {page_name: 'telegram/deposit-withdrawal'},
          });

          const depositAmountNumber = Number(values.depositAmount);
          const {depositAddress, network} = values;
          // TODO @@ cant get deposit address
          console.log(
            ' onFinish - depositAmountNumber, depositAddress, memo, walletAddress:',
            depositAmountNumber,
            depositAddress,
            memo,
            walletAddress
          );
          // TODO @@ set proper decimals
          // const usdtSmallestUnits = depositAmountNumber * 10 ** (isProd ? 6 : 9); // Convert to smallest units (6 decimals)
          const usdtSmallestUnits = depositAmountNumber * 10 ** jettonDecimals; // Convert to smallest units (6 decimals)
          // return
          // TODO @@ telegram - remember for tg user only
          if (wallet && selectedToken === TON_DEFAULT_TOKEN) {
            const forwardPayload = beginCell()
              .storeUint(0, 32) // 0 opcode means we have a comment
              .storeStringTail(memo)
              .endCell();

            const body = beginCell()
              .storeUint(0xf8a7ea5, 32) // opcode for jetton transfer
              .storeUint(0, 64) // query id
              // TODO @@ double check it see it toNano is needed
              .storeCoins(usdtSmallestUnits) // amount:(VarUInteger 16) -  Jetton amount for transfer (decimals = 6 - USDT, 9 - default). Function toNano use decimals = 9 (remember it)
              // TODO @@ cant get deposit address for TonConnect
              .storeAddress(Address.parse(depositAddress || walletAddress)) // destination:MsgAddress
              .storeAddress(Address.parse(userFriendlyAddress)) // response_destination:MsgAddress
              .storeBit(0) // no custom payload
              .storeCoins(toNano('0.05')) // forward_ton_amount:(VarUInteger 16) - if >0, will send notification message
              .storeBit(1) // we store forwardPayload as a reference
              .storeRef(forwardPayload)
              .endCell();

            const myTransaction = {
              // returnStrategy: 'https://t.me/devTgRandomBot',
              validUntil: Math.floor(Date.now() / 1000) + 60, // 60 sec
              messages: [
                {
                  address: jettonWalletAddress, // sender jetton wallet
                  // * cant process if amount is too low
                  amount: toNano('0.5').toString(), // for commission fees, excess will be returned
                  payload: body.toBoc().toString('base64'), // payload with jetton transfer body
                },
              ],
            };

            const result = await tonConnectUI.sendTransaction(myTransaction);

            if (result) {
              notification.success({
                message: t('deposit_pending_title'),
                description: t('deposit_process_message_1'),
              });

              setTimeout(() => {
                if (user?.isTelegramBrowser) {
                  router.back();
                } else {
                  system?.dismissModalSheet();
                }
              }, 500);
            }
          }
        }}
      >
        <Form.Item
          name="blockchain"
          label={<span className={classes.formItemLabel}>{t('currency')}</span>}
          rules={[
            {
              required: true,
              message: t('blockchain_required'),
            },
          ]}
        >
          <CustomSelect
            className={classes.customSelect}
            options={[
              {
                label: (
                  <div className={classes.option}>
                    <div className={classes.coinArea}>
                      <img
                        src="/icon/cryptocurrency/usdt.svg"
                        alt="USD"
                        className={classes.coinIcon}
                      />
                      {t('currency_usdt')}
                    </div>
                    <div className={classes.optionBalance}>
                      <Statistic
                        valueStyle={{
                          color: 'rgba(255, 255, 255, 0.5)',
                          fontSize: 14,
                          fontWeight: 400,
                        }}
                        value={user?.getCoinWalletBalance()}
                      />
                    </div>
                  </div>
                ),
                value: 'usdt',
              },
            ]}
          />
        </Form.Item>
        {/* network */}
        <Form.Item
          name="network"
          label={<span className={classes.formItemLabel}>{t('network')}</span>}
          rules={[
            {
              required: true,
            },
          ]}
        >
          {/* <CustomSelect className={classes.customSelect} options={NETWORK_OPTION} /> */}
          <CustomSelect
            className={classes.customSelect}
            // options={NETWORK_OPTION}
            options={networks?.map(item => ({
              label: <span>{`${item?.network} - ${getTokenSymbol(item?.networkTokenId)}`}</span>,
              value: item?.networkTokenId,
            }))}
            onChange={async value => {
              const selectedNetwork = networks?.find(item => item?.networkTokenId === value);
              setSelectedToken(selectedNetwork?.networkTokenId);

              const result = await getDepositAddress({networkTokenId: value});
              console.log(`@@ getDepositAddress - ${value}`, result?.data);
              // console.log('selectedNetwork?.networkTokenId', selectedNetwork?.networkTokenId);
              // switchChain({chainId: selectedNetwork?.networkTokenId === 'USDT_BSC' ? 56 : 1});
            }}
          />
        </Form.Item>

        {selectedToken === TON_DEFAULT_TOKEN ? (
          <>
            <div style={{display: 'flex', justifyContent: 'space-between', width: '100%'}}>
              <span className={classes.formItemLabel} style={{textAlign: 'left'}}>
                {t('deposit_method')}
              </span>
            </div>
            <DepositMethodSwitch toggleSwitch={toggleSwitch} isManualDeposit={isManualDeposit} />
            <EmbossedContainer className={classes.tonConnectOrQrCodeArea}>
              {!isManualDeposit ? (
                <>
                  <Row className={classes.walletBalanceRow}>
                    <Col className={classes.walletAmountCol}>{t('wallets_usdt_token')}</Col>

                    <Col>{wallet && usdtTokenBalance ? `${usdtTokenBalance} USDT` : ' -- '}</Col>
                  </Row>
                  <div
                    onClick={e => {
                      e.preventDefault(); // Prevent unintended form submission
                      console.log('TonConnectButton clicked');
                    }}
                  >
                    <TonConnectButton className={classes.tonConnectButton} />
                  </div>
                </>
              ) : (
                <section className={classes.tonQrCodeArea}>
                  <div className={classes.qrCodeAndConnectWalletButtonArea}>
                    <div>
                      {depositAddress?.walletAddress ? (
                        <QRCode
                          style={{width: 135, height: 135}}
                          value={depositAddress?.walletAddress}
                          logoImage="/icon/deposit-withdraw/usdt.svg"
                          removeQrCodeBehindLogo
                          logoPaddingStyle="circle"
                          logoPadding={2}
                        />
                      ) : (
                        <div className={classes.spinContainer}>
                          <Spin
                            size="large"
                            indicator={<LoadingOutlined style={{color: 'white'}} />}
                          />
                        </div>
                      )}
                    </div>
                  </div>
                  <Form.Item
                    name="depositAddress"
                    label={<span className={classes.formItemLabel}>{t('deposit_address')}</span>}
                  >
                    <CustomInput
                      disabled
                      suffix={
                        <CopyOutlined
                          onClick={() => handleCopyClick(form.getFieldValue('depositAddress'))}
                          className={classes.copyIcon}
                        />
                      }
                    />
                  </Form.Item>
                  <div style={{display: 'flex', justifyContent: 'space-between', width: '100%'}}>
                    <span className={classes.formItemLabel} style={{textAlign: 'left'}}>
                      {t('remark')}
                    </span>
                  </div>

                  <CustomInput
                    value={memo}
                    disabled
                    suffix={
                      <CopyOutlined
                        onClick={() => handleCopyClick(memo)}
                        className={classes.copyIcon}
                      />
                    }
                  />
                  <div className={classes.tonReminder}>
                    <span>{t('memo_reminder_1', {memo})}</span>
                  </div>
                  {/* <img src="/images-mobile/telegram/memo.webp" style={{width: '100%'}} /> */}
                </section>
              )}
            </EmbossedContainer>
            {!isManualDeposit && wallet && (
              <>
                <Form.Item
                  name="depositAmount"
                  label={<span className={classes.formItemLabel}>{t('deposit_amount')}</span>}
                  rules={[
                    {
                      required: true,
                      message: t('amount_required'),
                    },
                    {
                      validator: (rule, value, callback) => {
                        if (BigNumber(value).isGreaterThan(usdtTokenBalance)) {
                          callback(t('amount_exceeds_available'));
                        } else if (Number.isNaN(Number(value))) {
                          callback(t('invalid_number'));
                        } else {
                          callback();
                        }
                      },
                    },
                  ]}
                >
                  <CustomInput
                    placeholder={t('deposit_amount_placeholder')}
                    className={classes.depositAmountInput}
                    suffix={
                      <GradientButton
                        style={{height: 40, borderRadius: 5, margin: 0}}
                        onClick={() => {
                          form.setFieldsValue({depositAmount: usdtTokenBalance});
                        }}
                      >
                        {t('max')}
                      </GradientButton>
                    }
                  />
                </Form.Item>
                <div className={classes.buttonArea} style={{marginTop: 20}}>
                  <GradientButton
                    // onClick={() => {
                    // form.submit();
                    // }}
                    // disabled
                    disabled={wallet && !usdtTokenBalance}
                    // loading={isMakingTransaction}
                    style={{height: 50, margin: 0}}
                    htmlType="submit"
                    className={classes.depositButton}
                  >
                    {t('deposit')}
                  </GradientButton>
                </div>
              </>
            )}
          </>
        ) : (
          <>
            <Form.Item
              name="depositAddress"
              label={<span className={classes.formItemLabel}>{t('deposit_address')}</span>}
            >
              <CustomInput
                disabled
                suffix={<CopyOutlined onClick={() => handleCopyClick(form.getFieldValue('depositAddress'))} className={classes.copyIcon} />}
              />
            </Form.Item>
            <section className={classes.qrCodeArea}>
              {selectedToken !== 'USDT_TON' && memo && (
                <div style={{marginBottom: 10, color: 'red', textAlign: 'center'}}>
                  <div>{t('memo_reminder_1', {memo})}</div>
                </div>
              )}
              {depositAddress?.walletAddress ? (
                <QRCode
                  style={{width: 135, height: 135}}
                  value={depositAddress?.walletAddress}
                  logoImage="/icon/deposit-withdraw/usdt.svg"
                  removeQrCodeBehindLogo
                  logoPaddingStyle="circle"
                  logoPadding={2}
                />
              ) : (
                <div className={classes.spinContainer}>
                  <Spin size="large" indicator={<LoadingOutlined style={{color: 'white'}} />} />
                </div>
              )}
              <div className={classes.exchangeRateContainer}>
                <span>{`${t('exchange_rate')} (USDT/USD): `}</span>
                <span className={classes.exchangeRate}>{`${depositInfo?.usdtRate || '--'}`}</span>
              </div>
              <div className={classes.depositDescription}>
                <p className={classes.formItemLabel}>{t('deposit_process_message_1')}</p>
              </div>
            </section>
          </>
        )}
      </Form>
    </section>
  );
};

export default TelegramContent;
